import keys from 'lodash/keys.js';
import merge from 'lodash/merge.js';

import axios from '@/plugins/axios.js';
import SlideDeck from '@/store/modules/slidedecks/SlideDeck.js';
import Collection from '@/store/quill/Collection.js';
import EdverumTable from '@/store/utilities/EdverumTable.js';

const collectionHelpers = EdverumTable.collection('slideDeck');

export default merge(Collection('SlideDecks', SlideDeck), {
    state: {
        sort: {
            id: null,
            title: null,
            description: null,
            updated_at: null,
        },
    },

    getters: {
        ...collectionHelpers.getters,
    },

    actions: {
        clearSort: collectionHelpers.actions.clearSort,

        addSlideDeck: collectionHelpers.actions.addModel(),

        paginateAndFetch: collectionHelpers.actions.paginateAndFetch,

        fetch: collectionHelpers.actions.fetch(({ context, queryBuilder }) => {
            const { getters, rootGetters } = context;
            queryBuilder
                .include(['tags', 'groups', 'courseCodes'])
                .sort(getters.queryBuilderSorting)
                .setPagination(getters.paginate);

            if (rootGetters['SlideDecksView/hasQuery']) {
                queryBuilder.raw({
                    search: rootGetters['SlideDecksView/searchQuery'],
                });
            }
            if (rootGetters['SlideDecksView/hasTagSearch']) {
                queryBuilder.raw({
                    tags: rootGetters['SlideDecksView/searchTag'],
                });
            }
            if (rootGetters['SlideDecksView/hasGroupSearch']) {
                queryBuilder.raw({
                    groups: rootGetters['SlideDecksView/searchGroup'],
                });
            }
            if (rootGetters['SlideDecksView/hasViewArchive']) {
                queryBuilder.raw({
                    view_archived: rootGetters['SlideDecksView/searchViewArchive'],
                });
            }

            // Request thumbnails for list view (performance optimization)
            queryBuilder.raw({
                with_thumbnail: 1,
            });
        }),

        fetchOne: collectionHelpers.actions.fetchOne(({ queryBuilder }) => {
            queryBuilder.include(['tags', 'groups', 'courseCodes']);
        }),

        updateTags: collectionHelpers.actions.updateModelTags(),

        updateCourseCodes: collectionHelpers.actions.updateModelCourseCodes(),

        updateGroups: collectionHelpers.actions.updateModelGroups(),

        removeChecked: collectionHelpers.actions.removeChecked(),

        async processSlideDeck(context, uuid) {
            const item = context.getters.get(uuid);

            const payload = new FormData();

            keys(item).forEach(key => payload.append(key, item[key]));

            const data = await context.dispatch('addSlideDeck', payload);

            if (item.tags) {
                await context.dispatch('updateTags', {
                    subject: data,
                    tags: item.tags,
                    modelKey: 'reference_id',
                });
            }

            if (item.course_codes) {
                await context.dispatch('updateCourseCodes', {
                    subject: data,
                    course_codes: item.course_codes,
                    modelKey: 'reference_id',
                });
            }

            if (item.groups) {
                await context.dispatch('updateGroups', {
                    subject: data,
                    groups: item.groups,
                    modelKey: 'reference_id',
                });
            }
            return data;
        },

        async syncCourseCodes(context, { slideDeck, paramKey, courseCodes }) {
            await context.dispatch('AutoSave/setSaving', `${Collection}/updateGroups`, { root: true });

            await axios.put(`/api/slide-decks/${slideDeck.reference_id}`, {
                reference_id: slideDeck.reference_id,
                title: slideDeck.title,
                [paramKey]: courseCodes,
            });

            await context.dispatch('AutoSave/setSaved', `${Collection}/updateGroups`, { root: true });
        },

        /**
         * @param context
         * @param filter
         * @return {Promise<any>}
         */
        async filterAndFetch(context, filter) {
            try {
                await context.dispatch('filter', filter);
                await context.dispatch('purge');
                await context.dispatch('fetch');
            } catch (error) {
                return error;
            }
        },

        /**
         * Retry processing a failed slide deck upload
         * @param context
         * @param referenceId - The slide deck reference ID
         * @return {Promise<any>}
         */
        async retry(context, referenceId) {
            const { data } = await axios.post(`/api/slide-decks/${referenceId}/retry`);
            return data;
        },
    },

    mutations: {
        UPDATE_TAGS: collectionHelpers.mutations.UPDATE_TAGS,
        UPDATE_GROUPS: collectionHelpers.mutations.UPDATE_GROUPS,
        UPDATE_COURSE_CODES: collectionHelpers.mutations.UPDATE_COURSE_CODES,
        CLEAR_SORTING: collectionHelpers.mutations.CLEAR_SORTING,

        AUTO_SAVE() {
            // Use to trigger auto save
        },
    },
});
